import { PositionHelper } from '../position-helper';
import { ApplicationStateService } from '../../services/application-state.service';
import { Page } from '../../models/page';
import { Rect } from '../../models/rect';
import { PageEditMode } from './page-edit-mode';
import { PageEditModeStatus } from './page-edit-mode-status';
import { Position } from '../../models/position';
import { RectService } from '../../services/rect.service';
import { PageComponent } from '../../components/bookviewer/page/page.component';
import { ColorPickerOptions } from '../../../environments/configs/color-picker-options';

export class RectPageEditMode implements PageEditMode {

    status: PageEditModeStatus;
    readonly type = 'RECT';
    private applicationStateService: ApplicationStateService;
    private rectService: RectService;
    private page: Page;
    private parentDiv: JQuery;
    private rect: Rect;
    private basePosition: Position;
    private timeoutId;

    constructor(applicationStateService: ApplicationStateService,
                rectService: RectService) {

        this.applicationStateService = applicationStateService;
        this.rectService = rectService;
        this.status = PageEditModeStatus['INIT'];
    }

    static getClassName(): string {
        return new RectPageEditMode(
            undefined,
            undefined).constructor.name;
    }

    /**
     * Set page for mode.
     *
     * @param {PageComponent} pageComponent
     */
    setPage(pageComponent: PageComponent): void {
        this.page = pageComponent.page;
        this.parentDiv = pageComponent.getContainer();
    }

    /**
     * Handler for mousedown/touchstart event.
     *
     * @param {Event} $event
     */
    onMouseDown($event): void {
        if (!this.applicationStateService.isPageEditable) {
            return;
        }

        if (this.status !== PageEditModeStatus['INIT']) {
            this.onMouseUp($event);

            return;
        }

        const parentDiv = this.parentDiv;
        this.rect = new Rect();
        this.rect.pageId = this.page.id;
        this.rect.open = false;
        this.rect.color = ColorPickerOptions.noteColors[1];
        this.rect.position = PositionHelper.transformPositionFromPixelToPercent(
            PositionHelper.calculateRelativePositionInPixel($event, parentDiv),
            parentDiv
        );
        this.basePosition = this.rect.position;
        this.status = PageEditModeStatus['PENDING'];
    }

    /**
     * Handler for mousemove/touchmove event.
     *
     * @param {Event} $event
     */
    onMouseMove($event): void {
        if (this.rect) {
            this.rect.open = false;
        }

        if (!this.applicationStateService.isPageEditable ||
            this.status === PageEditModeStatus['INIT'] ||
            this.status === PageEditModeStatus['FINISHED'] ||
            !this.rect.pageId ||
            this.rect.pageId !== this.page.id
        ) {
            return;
        }

        const parentDiv = this.parentDiv;
        const currentPoint = PositionHelper.transformPositionFromPixelToPercent(
            PositionHelper.calculateRelativePositionInPixel($event, parentDiv),
            parentDiv
        );

        currentPoint.x = Math.max(0, currentPoint.x);
        currentPoint.y = Math.max(0, currentPoint.y);

        this.rect.position = PositionHelper.calculateDistance(this.basePosition, currentPoint);

        this.rect.position.w = Math.min(this.rect.position.w, 100 - this.rect.position.x);
        this.rect.position.h = Math.min(this.rect.position.h, 100 - this.rect.position.y);

        this.status = PageEditModeStatus['PENDING'];
    }

    /**
     * Handler for mouseup/touchend event.
     *
     * @param {Event} $event
     */
    onMouseUp($event): void {
        if (this.status !== PageEditModeStatus['PENDING'] || this.rect.pageId !== this.page.id) {
            return;
        }

        this.status = PageEditModeStatus['FINISHED'];

        if (this.rect) {
            (this.rect as any).isNew = true;
            this.rectService.storeRectLocally(this.rect);
            setTimeout(() => {
                this.applicationStateService.isPageEditable = false;
            }, 500);

            return;
        }
    }

    onMouseLeave($event): void {
        if (!this.applicationStateService.isPageEditable) {
            return;
        }
        this.onMouseMove($event);
    }

    onPinch($event): void {

        if (!this.applicationStateService.isPageEditable ||
            this.status === PageEditModeStatus['FINISHED']) {
            return;
        }

        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }

        if (!this.rect) {
            this.rect = new Rect();
            this.rect.pageId = this.page.id;
        }

        const parentDiv = this.parentDiv;

        const point1 = Position.createEmptyPosition();
        point1.x = $event.changedPointers[0].clientX;
        point1.y = $event.changedPointers[0].clientY;

        const point2 = Position.createEmptyPosition();
        point2.x = $event.changedPointers[1].clientX;
        point2.y = $event.changedPointers[1].clientY;

        this.rect.position = Position.createEmptyPosition();
        this.rect.position.x = Math.min(point1.x, point2.x);
        this.rect.position.y = Math.min(point1.y, point2.y);

        this.rect.position.w = Math.max(point1.x, point2.x) - this.rect.position.x;
        this.rect.position.h = Math.max(point1.y, point2.y) - this.rect.position.y;

        this.rect.position = PositionHelper.transformPositionFromPixelToPercent(
            PositionHelper.calculateRelativePositionInPixelOfPoint(this.rect.position, parentDiv),
            parentDiv
        );

        this.rect.position.w = this.rect.position.w * $event.scale;
        this.rect.position.h = this.rect.position.h * $event.scale;

        this.rect.position.w = Math.min(this.rect.position.x, 100 - this.rect.position.x);
        this.rect.position.h = Math.min(this.rect.position.y, 100 - this.rect.position.y);

        this.status = PageEditModeStatus['PENDING'];

        this.timeoutId = setTimeout(() => {
            this.onMouseUp($event);

        }, 1000);

    }

    getResult(): Rect {
        return this.rect;
    }

    getStatus(): PageEditModeStatus {
        return this.status;
    }

    getPage(): Page {
        return this.page;
    }
}
